import { useState, type FormEvent } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useRessource } from '../../hooks/useRessource';
import { ErreurApi, api } from '../../lib/apiClient';
import {
  Badge,
  Champ,
  Chargement,
  EnTetePage,
  Erreur,
  Infobulle,
  Modale,
  Tableau,
  type Colonne,
} from '../../components/Elements';
import { PiedFormulaire } from '../ReferentielsPages';
import { formaterDateHeure } from '../../lib/format';
import type { CodeRole, Entite, SessionOuverte, Utilisateur } from '../../types';

/**
 * Ce que fait chaque role livre avec la plateforme, en une phrase. Ce ne
 * sont PLUS les roles disponibles — depuis 0.37.0 la liste vient de
 * `/api/admin/roles`, sans quoi un role cree a l'ecran Roles resterait
 * inattribuable. Un role personnalise n'a pas de description : c'est son
 * libelle, choisi par qui l'a cree, qui le dit.
 */
const DESCRIPTIONS: { code: CodeRole; libelle: string; description: string }[] = [
  {
    code: 'super_admin',
    libelle: 'Super admin',
    description:
      'Toutes les permissions, y compris celles ajoutees plus tard. A n’attribuer qu’aux personnes qui repondent de la plateforme entiere.',
  },
  {
    code: 'admin_technique',
    libelle: 'Admin technique',
    description: 'Administre la plateforme, pas les donnees. Aucun acces metier sans elevation.',
  },
  {
    code: 'direction',
    libelle: 'Direction',
    description: 'Pilotage complet de l’entite, cloture d’annee, purge RGPD.',
  },
  {
    code: 'gestionnaire',
    libelle: 'Gestionnaire',
    description: 'Saisie quotidienne. Aucune suppression definitive, aucune cloture.',
  },
  {
    code: 'professeur',
    libelle: 'Professeur',
    description: 'Ses seuls creneaux et eleves. Aucun montant, aucune adresse.',
  },
  {
    code: 'lecteur',
    libelle: 'Lecteur',
    description: 'Tableaux de bord agreges, sans donnee nominative.',
  },
];

/**
 * Le navigateur, tire de la chaine `user-agent`.
 *
 * Reconnaissance GROSSIERE et assumee : l'ordre des tests fait tout, un
 * Edge se declarant aussi Chrome et Safari, et Chrome se declarant
 * Safari. On ne cherche pas l'exactitude — juste de quoi reconnaitre son
 * propre poste dans une liste de quatre lignes.
 */
function navigateur(userAgent: string | null): string {
  if (!userAgent) return '—';
  const chaine = [
    ['Edg/', 'Edge'],
    ['OPR/', 'Opera'],
    ['Firefox/', 'Firefox'],
    ['Chrome/', 'Chrome'],
    ['Safari/', 'Safari'],
  ].find(([marqueur]) => userAgent.includes(marqueur!));
  return chaine ? chaine[1]! : 'Autre';
}

/** « 4 min », « 2 h 05 » — une duree se lit mieux qu'un horodatage. */
function depuis(minutes: number): string {
  if (minutes < 1) return "a l'instant";
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * Les sessions ouvertes, en consultation seule.
 *
 * Le mot « ouverte » est plus juste que « connectee » : une session
 * survit a l'inactivite jusqu'a la requete suivante, qui la refuse. Une
 * ligne DORMANTE designe donc quelqu'un deja deconnecte qui ne le sait
 * pas encore — et c'est l'information utile avant un deploiement.
 */
function SessionsOuvertes() {
  const { session } = useAuth();
  const { donnees, chargement, erreur, recharger } = useRessource(
    () =>
      api.get<{ seuilInactiviteMinutes: number; sessions: SessionOuverte[] }>(
        '/api/admin/sessions',
      ),
    [],
  );

  const sessions = donnees?.sessions ?? [];
  const eveillees = sessions.filter((s) => !s.dormante);

  const colonnes: Colonne<SessionOuverte>[] = [
    {
      cle: 'utilisateur',
      entete: 'Utilisateur',
      rendu: (s) => (
        <span className="flex items-center gap-x-2 whitespace-nowrap">
          <span className="font-medium">
            {s.prenom} {s.nom.toUpperCase()}
          </span>
          {s.utilisateur_id === session?.utilisateur.id && <Badge>vous</Badge>}
          {s.dormante && <Badge ton="alerte">dormante</Badge>}
        </span>
      ),
    },
    {
      cle: 'activite',
      entete: 'Derniere activite',
      rendu: (s) => (
        <Infobulle
          texte={formaterDateHeure(s.derniere_activite)}
          className={s.dormante ? 'text-encre-400' : 'font-medium text-encre-800'}
        >
          {depuis(s.inactivite_minutes)}
        </Infobulle>
      ),
    },
    {
      cle: 'entite',
      entete: 'Ecole',
      rendu: (s) => (s.entite_code ? <Badge>{s.entite_code}</Badge> : <span className="text-xs text-encre-400">—</span>),
    },
    {
      cle: 'origine',
      entete: 'Origine',
      rendu: (s) => (
        <Infobulle texte={s.user_agent} className="whitespace-nowrap text-xs text-encre-600">
          {s.ip ?? '—'} · {navigateur(s.user_agent)}
        </Infobulle>
      ),
    },
    {
      cle: 'ouverte',
      entete: 'Ouverte le',
      rendu: (s) => <span className="text-xs text-encre-500">{formaterDateHeure(s.created_at)}</span>,
    },
    {
      cle: 'expire',
      entete: 'Expire le',
      rendu: (s) => <span className="text-xs text-encre-500">{formaterDateHeure(s.expire_le)}</span>,
    },
  ];

  return (
    <section className="mt-8">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-3">
        <h2 className="text-lg font-medium text-encre-900">Sessions ouvertes</h2>
        {donnees && (
          <p className="text-sm text-encre-500">
            <span className="tabulaire font-medium text-encre-800">{eveillees.length}</span> active
            {eveillees.length > 1 ? 's' : ''}
            {sessions.length > eveillees.length &&
              `, ${sessions.length - eveillees.length} dormante${
                sessions.length - eveillees.length > 1 ? 's' : ''
              }`}
          </p>
        )}
        <button type="button" className="ml-auto text-xs underline" onClick={recharger}>
          Rafraichir
        </button>
      </div>

      <p className="mb-3 max-w-3xl text-xs text-encre-500">
        Une session <strong>dormante</strong> a depasse le seuil d’inactivite
        {donnees ? ` de ${donnees.seuilInactiviteMinutes} minutes` : ''} : son porteur est deja
        deconnecte, il le constatera a son prochain geste. Consultation seule — cet ecran n’a pas
        de bouton pour couper une session.
      </p>

      {erreur && <Erreur message={erreur} onReessayer={recharger} />}
      {chargement ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={sessions}
            cleDe={(s) => s.id}
            messageVide="Aucune session ouverte."
          />
        </div>
      )}
    </section>
  );
}

export function UtilisateursPage() {
  const { session, peut } = useAuth();
  const [modaleCreation, setModaleCreation] = useState(false);
  const [habilitationPour, setHabilitationPour] = useState<Utilisateur | null>(null);

  const utilisateurs = useRessource(() => api.get<Utilisateur[]>('/api/admin/utilisateurs'), []);
  const entites = useRessource(() => api.get<Entite[]>('/api/admin/entites'), []);

  const changerStatut = async (utilisateur: Utilisateur) => {
    const suspendre = utilisateur.statut === 'actif';

    if (
      suspendre &&
      !window.confirm(
        `Suspendre ${utilisateur.prenom} ${utilisateur.nom} ?\n\n` +
          `Ses sessions en cours seront invalidees immediatement.`,
      )
    ) {
      return;
    }

    try {
      await api.patch(`/api/admin/utilisateurs/${utilisateur.id}/statut`, {
        statut: suspendre ? 'suspendu' : 'actif',
      });
      utilisateurs.recharger();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Operation impossible.');
    }
  };

  const mettreFinHabilitation = async (habilitationId: string) => {
    try {
      await api.post(`/api/admin/habilitations/${habilitationId}/fin`);
      utilisateurs.recharger();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Operation impossible.');
    }
  };

  const basculerFinances = async (habilitationId: string, accesFinances: boolean) => {
    try {
      await api.patch(`/api/admin/habilitations/${habilitationId}/finances`, { accesFinances });
      utilisateurs.recharger();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Operation impossible.');
    }
  };

  const colonnes: Colonne<Utilisateur>[] = [
    {
      cle: 'identite',
      entete: 'Utilisateur',
      rendu: (u) => (
        <div>
          <div className="font-medium">
            {u.prenom} {u.nom}
          </div>
          <div className="text-xs text-encre-400">
            {u.identifiant} · {u.email}
          </div>
        </div>
      ),
    },
    {
      cle: 'habilitations',
      entete: 'Habilitations',
      rendu: (u) => {
        const enCours = u.habilitations.filter(
          (h) => !h.date_fin || h.date_fin >= new Date().toISOString().slice(0, 10),
        );
        if (enCours.length === 0) {
          return <Badge ton="alerte">Aucune — n’accede a rien</Badge>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {enCours.map((habilitation) => (
              <span
                key={habilitation.id}
                className="inline-flex items-center gap-1 rounded-full bg-encre-100 px-2 py-0.5 text-xs"
              >
                <span className="font-medium">{habilitation.entite_code}</span>
                <span className="text-encre-500">{habilitation.role_libelle}</span>
                {/* Bascule du dashboard financier — RG-36 : jamais sur
                    ses propres habilitations, le serveur le refuse aussi. */}
                {peut('habilitation.attribuer') &&
                habilitation.utilisateur_id !== session?.utilisateur.id ? (
                  <Infobulle
                    texte={
                      habilitation.acces_finances
                        ? 'Dashboard financier accessible — cliquer pour le retirer'
                        : 'Dashboard financier non accessible — cliquer pour l’ouvrir'
                    }
                  >
                    <button
                      type="button"
                      className={
                        habilitation.acces_finances
                          ? 'font-semibold text-emerald-700 hover:text-encre-400'
                          : 'text-encre-300 hover:text-emerald-700'
                      }
                      onClick={(evenement) => {
                        evenement.stopPropagation();
                        void basculerFinances(habilitation.id, !habilitation.acces_finances);
                      }}
                    >
                      €
                    </button>
                  </Infobulle>
                ) : (
                  habilitation.acces_finances && (
                    <Infobulle texte="Dashboard financier accessible" className="font-semibold text-emerald-700">
                      €
                    </Infobulle>
                  )
                )}
                {peut('habilitation.revoquer') && habilitation.utilisateur_id !== session?.utilisateur.id && (
                  <Infobulle texte="Mettre fin a cette habilitation">
                    <button
                      type="button"
                      className="text-encre-400 hover:text-red-600"
                      aria-label="Mettre fin a cette habilitation"
                      onClick={(evenement) => {
                        evenement.stopPropagation();
                        void mettreFinHabilitation(habilitation.id);
                      }}
                    >
                      ✕
                    </button>
                  </Infobulle>
                )}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (u) => (
        <div className="space-y-1">
          <Badge
            ton={u.statut === 'actif' ? 'succes' : u.statut === 'suspendu' ? 'danger' : 'alerte'}
          >
            {u.statut}
          </Badge>
          {u.verrouille_jusqu_a && new Date(u.verrouille_jusqu_a) > new Date() && (
            <div>
              <Badge ton="danger">Verrouille</Badge>
            </div>
          )}
        </div>
      ),
    },
    {
      cle: 'connexion',
      entete: 'Derniere connexion',
      rendu: (u) => <span className="text-encre-500">{formaterDateHeure(u.derniere_connexion)}</span>,
    },
    {
      cle: 'actions',
      entete: '',
      classe: 'text-right',
      rendu: (u) => (
        <div className="flex justify-end gap-3">
          {peut('habilitation.attribuer') && u.id !== session?.utilisateur.id && (
            <button
              type="button"
              className="text-sm text-encre-500 underline hover:text-encre-900"
              onClick={() => setHabilitationPour(u)}
            >
              Habiliter
            </button>
          )}
          {peut('utilisateur.suspendre') && u.id !== session?.utilisateur.id && (
            <button
              type="button"
              className="text-sm text-encre-500 underline hover:text-encre-900"
              onClick={() => void changerStatut(u)}
            >
              {u.statut === 'actif' ? 'Suspendre' : 'Reactiver'}
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <EnTetePage
        titre="Utilisateurs"
        description="Les droits sont attribues par couple (utilisateur, entite). Un meme utilisateur peut etre professeur sur une entite et lecteur sur une autre. On met fin a une habilitation plutot que de supprimer un compte, pour preserver l’imputabilite de l’audit."
        actions={
          peut('utilisateur.creer') ? (
            <button type="button" className="bouton-principal" onClick={() => setModaleCreation(true)}>
              Nouvel utilisateur
            </button>
          ) : null
        }
      />

      {utilisateurs.erreur && (
        <Erreur message={utilisateurs.erreur} onReessayer={utilisateurs.recharger} />
      )}

      {utilisateurs.chargement ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={utilisateurs.donnees ?? []}
            cleDe={(u) => u.id}
            messageVide="Aucun utilisateur."
          />
        </div>
      )}

      {peut('utilisateur.lire') && <SessionsOuvertes />}

      <FormulaireUtilisateur
        ouverte={modaleCreation}
        onFermer={() => setModaleCreation(false)}
        onEnregistre={() => {
          setModaleCreation(false);
          utilisateurs.recharger();
        }}
      />

      <FormulaireHabilitation
        utilisateur={habilitationPour}
        entites={entites.donnees ?? []}
        onFermer={() => setHabilitationPour(null)}
        onEnregistre={() => {
          setHabilitationPour(null);
          utilisateurs.recharger();
        }}
      />
    </>
  );
}

function FormulaireUtilisateur({
  ouverte,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [valeurs, setValeurs] = useState({
    identifiant: '',
    email: '',
    nom: '',
    prenom: '',
    motDePasseInitial: '',
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await api.post('/api/admin/utilisateurs', {
        identifiant: valeurs.identifiant,
        email: valeurs.email,
        nom: valeurs.nom,
        prenom: valeurs.prenom,
        ...(valeurs.motDePasseInitial ? { motDePasseInitial: valeurs.motDePasseInitial } : {}),
      });
      setValeurs({ identifiant: '', email: '', nom: '', prenom: '', motDePasseInitial: '' });
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Creation impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Nouvel utilisateur" ouverte={ouverte} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Prenom">
            <input
              className="champ"
              value={valeurs.prenom}
              onChange={(e) => setValeurs({ ...valeurs, prenom: e.target.value })}
              required
              autoFocus
            />
          </Champ>
          <Champ libelle="Nom">
            <input
              className="champ"
              value={valeurs.nom}
              onChange={(e) => setValeurs({ ...valeurs, nom: e.target.value })}
              required
            />
          </Champ>
        </div>

        <Champ libelle="Identifiant" aide="Lettres minuscules, chiffres, point, tiret ou tiret bas.">
          <input
            className="champ font-mono"
            value={valeurs.identifiant}
            onChange={(e) => setValeurs({ ...valeurs, identifiant: e.target.value.toLowerCase() })}
            required
          />
        </Champ>

        <Champ libelle="Email">
          <input
            className="champ"
            type="email"
            value={valeurs.email}
            onChange={(e) => setValeurs({ ...valeurs, email: e.target.value })}
            required
          />
        </Champ>

        <Champ
          libelle="Mot de passe initial (optionnel)"
          aide="Laissez vide pour creer un compte invite : il n’aura acces a rien tant qu’un mot de passe n’aura pas ete defini. Minimum 12 caracteres."
        >
          <input
            className="champ"
            type="password"
            value={valeurs.motDePasseInitial}
            onChange={(e) => setValeurs({ ...valeurs, motDePasseInitial: e.target.value })}
            autoComplete="new-password"
            minLength={12}
          />
        </Champ>

        <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
          Creer un compte ne lui donne aucun droit. Il faut ensuite lui attribuer une habilitation
          sur au moins une entite.
        </p>

        <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} libelle="Creer" />
      </form>
    </Modale>
  );
}

function FormulaireHabilitation({
  utilisateur,
  entites,
  onFermer,
  onEnregistre,
}: {
  utilisateur: Utilisateur | null;
  entites: Entite[];
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [entiteId, setEntiteId] = useState('');
  const [roleCode, setRoleCode] = useState<CodeRole>('gestionnaire');
  // Les roles viennent de l'API : la matrice est reglable, la liste des
  // roles attribuables ne peut donc plus etre figee dans cet ecran.
  const { donnees: matrice } = useRessource(
    () => api.get<{ roles: { id: string; code: string; libelle: string }[] }>('/api/admin/roles'),
    [],
  );
  const roles = matrice?.roles ?? DESCRIPTIONS.map(({ code, libelle }) => ({ id: code, code, libelle }));
  // Ferme par defaut : le dashboard Activite est le socle, le financier
  // s'ouvre explicitement.
  const [accesFinances, setAccesFinances] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (!utilisateur) return;
    setErreur(null);
    setEnCours(true);
    try {
      await api.post('/api/admin/habilitations', {
        utilisateurId: utilisateur.id,
        entiteId,
        roleCode,
        accesFinances,
      });
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Attribution impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const descriptionRole = DESCRIPTIONS.find((role) => role.code === roleCode)?.description;

  return (
    <Modale
      titre={utilisateur ? `Habiliter ${utilisateur.prenom} ${utilisateur.nom}` : ''}
      ouverte={Boolean(utilisateur)}
      onFermer={onFermer}
    >
      <form onSubmit={soumettre} className="space-y-4">
        <Champ libelle="Entite">
          <select
            className="champ"
            value={entiteId}
            onChange={(e) => setEntiteId(e.target.value)}
            required
          >
            <option value="">— Choisir —</option>
            {entites
              .filter((entite) => entite.actif)
              .map((entite) => (
                <option key={entite.id} value={entite.id}>
                  {entite.code} — {entite.raison_sociale}
                </option>
              ))}
          </select>
        </Champ>

        <Champ libelle="Role" aide={descriptionRole}>
          <select
            className="champ"
            value={roleCode}
            onChange={(e) => setRoleCode(e.target.value as CodeRole)}
          >
            {roles.map((role) => (
              <option key={role.id} value={role.code}>
                {role.libelle}
              </option>
            ))}
          </select>
        </Champ>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={accesFinances}
            onChange={(e) => setAccesFinances(e.target.checked)}
          />
          <span>
            Accès au dashboard financier
            <span className="mt-0.5 block text-xs text-encre-400">
              Le dashboard Activité (effectifs, sans montant) vient avec le rôle. Celui des
              finances s’ouvre ici, et se retire de la même façon.
            </span>
          </span>
        </label>

        <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
          Un utilisateur ne detient qu’un role par entite. Attribuer un role sur une entite ou il en
          a deja un sera refuse : mettez d’abord fin a l’habilitation existante.
        </p>

        <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} libelle="Attribuer" />
      </form>
    </Modale>
  );
}
